import React, { useMemo, useState } from "react";
import {
  Chip,
  Tooltip,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Stack,
  Typography,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

/**
 * Resume score display helpers.
 *
 * The API returns the raw score the RAG service stores: an integer 1-10. Converting it
 * to a percentage is purely a presentation choice, so it happens here and nowhere else —
 * the database and the RAG service keep working in 1-10.
 *
 * Note the floor is 10%, not 0%: a scored candidate can never be below 1/10. An empty
 * cell means "not scored yet", which is different from "scored badly".
 */

export const scoreToPercent = (score) => {
  const n = Number(score);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 10) : null;
};

/** Green = strong match, amber = partial, red = weak. Thresholds are on the percentage. */
export const scoreColor = (percent) => {
  if (percent === null) return { color: "#6b7280", bg: "#f3f4f6", border: "#e5e7eb" };
  if (percent >= 70) return { color: "#166534", bg: "#dcfce7", border: "#86efac" };
  if (percent >= 40) return { color: "#92400e", bg: "#fef3c7", border: "#fcd34d" };
  return { color: "#991b1b", bg: "#fee2e2", border: "#fca5a5" };
};

/**
 * The backend writes explanationHtml as a fixed sequence of plain tags (see
 * resume_scorer.py:_build_html) — not a format meant for direct display, just the
 * source the candidate's selection email is built from. We parse that fixed shape into
 * data here so the dialog can render it as real UI (pills, cards, chips) instead of a
 * dumped email body. Any shape mismatch (backend changes, older/malformed rows) returns
 * null and the dialog falls back to the plain status/reason view.
 */
const parseSkillGroups = (ulEl) => {
  if (!ulEl) return [];
  return Array.from(ulEl.querySelectorAll("li")).map((li) => {
    const strong = li.querySelector("strong");
    const category = strong ? strong.textContent.replace(/:\s*$/, "").trim() : null;
    const clone = li.cloneNode(true);
    clone.querySelector("strong")?.remove();
    const itemsText = clone.textContent.replace(/^:\s*/, "").trim();
    const items = itemsText ? itemsText.split(",").map((s) => s.trim()).filter(Boolean) : [];
    return { category, items };
  }).filter((group) => group.items.length > 0);
};

const parseExplanationSummary = (html) => {
  if (!html || typeof DOMParser === "undefined") return null;
  try {
    const body = new DOMParser().parseFromString(html, "text/html").body;
    const c = Array.from(body.children);
    if (c.length < 11) return null;
    const eligibility = c[5].textContent.trim();
    return {
      status: c[1].textContent.trim(),
      reasonLabel: c[2].textContent.replace(/:\s*$/, "").trim(),
      reason: c[3].textContent.trim(),
      eligibility,
      isEligible: eligibility.startsWith("Eligible"),
      have: parseSkillGroups(c[8]),
      missingLabel: c[9].textContent.replace(/^❌\s*/, "").replace(/:\s*$/, "").trim(),
      missing: parseSkillGroups(c[10]),
    };
  } catch {
    return null;
  }
};

const statusTone = (status) => {
  const selected = String(status).toLowerCase() === "selected";
  return selected
    ? { color: "#166534", bg: "#dcfce7", border: "#86efac" }
    : { color: "#991b1b", bg: "#fee2e2", border: "#fca5a5" };
};

/** A labelled block of prose — the reason and eligibility rows share this shape. */
const SummaryRow = ({ label, icon, children }) => (
  <Box>
    <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5}>
      {icon}
      <Typography variant="overline" sx={{ lineHeight: 1, color: "text.secondary", fontWeight: 700 }}>
        {label}
      </Typography>
    </Stack>
    <Typography variant="body2" sx={{ color: "text.primary" }}>
      {children}
    </Typography>
  </Box>
);

/** "What They Have" / "Missing Skills" — grouped skill chips on a tinted card. */
const SkillGroupCard = ({ title, icon, groups, tone }) => {
  const palette = tone === "have"
    ? { bg: "#f0fdf4", border: "#bbf7d0", chipBg: "#dcfce7", chipColor: "#166534" }
    : { bg: "#fef2f2", border: "#fecaca", chipBg: "#fee2e2", chipColor: "#991b1b" };

  return (
    <Box sx={{ bgcolor: palette.bg, border: `1px solid ${palette.border}`, borderRadius: 2, p: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
        {icon}
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Stack>
      {groups.length === 0 || (groups.length === 1 && !groups[0].category && /^no /i.test(groups[0].items[0])) ? (
        <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
          {groups[0]?.items[0] || "None"}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {groups.map((group, i) => (
            <Box key={i}>
              {group.category && (
                <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontWeight: 600, mb: 0.5 }}>
                  {group.category}
                </Typography>
              )}
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {group.items.map((skill, j) => (
                  <Chip
                    key={j}
                    label={skill}
                    size="small"
                    sx={{ bgcolor: palette.chipBg, color: palette.chipColor, fontWeight: 500 }}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

/**
 * Full score breakdown, opened by clicking a scored chip.
 * Prefers explanationHtml (the same write-up the candidate's selection email carries —
 * education eligibility + skill-gap analysis), rendered as real UI; falls back to
 * status/reason alone for candidates scored before that field was captured, or if the
 * HTML doesn't parse into the expected shape.
 */
const ResumeScoreSummaryDialog = ({ open, onClose, status, reason, explanationHtml }) => {
  const parsed = useMemo(() => parseExplanationSummary(explanationHtml), [explanationHtml]);
  const tone = statusTone(parsed?.status ?? status);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderTop: `4px solid ${tone.color}`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Summary
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#fafafa" }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700 }}>
              Selection Status
            </Typography>
            <Box mt={0.5}>
              <Chip
                icon={
                  String(parsed?.status ?? status).toLowerCase() === "selected"
                    ? <CheckCircleIcon sx={{ color: `${tone.color} !important` }} />
                    : <CancelIcon sx={{ color: `${tone.color} !important` }} />
                }
                label={parsed?.status ?? status ?? "—"}
                sx={{
                  color: tone.color,
                  backgroundColor: tone.bg,
                  border: `1px solid ${tone.border}`,
                  fontWeight: 700,
                }}
              />
            </Box>
          </Box>

          <SummaryRow label={parsed ? `Reason for ${parsed.reasonLabel}` : "Reason"}>
            {parsed?.reason ?? reason ?? "No further detail available"}
          </SummaryRow>

          {parsed && (
            <>
              <SummaryRow
                label="Education Eligibility Check"
                icon={parsed.isEligible
                  ? <CheckCircleIcon fontSize="small" sx={{ color: "#16a34a" }} />
                  : <CancelIcon fontSize="small" sx={{ color: "#dc2626" }} />}
              >
                {parsed.eligibility}
              </SummaryRow>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.5, mb: 1.5 }}>
                  SKILL GAP ANALYSIS
                </Typography>
                <Stack spacing={1.5}>
                  <SkillGroupCard
                    title="What They Have"
                    icon={<CheckCircleIcon fontSize="small" sx={{ color: "#16a34a" }} />}
                    groups={parsed.have}
                    tone="have"
                  />
                  <SkillGroupCard
                    title={parsed.missingLabel}
                    icon={<CancelIcon fontSize="small" sx={{ color: "#dc2626" }} />}
                    groups={parsed.missing}
                    tone="missing"
                  />
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Table cell content for a candidate's resume score.
 * Renders "–" when the candidate has not been scored (older profiles, or a resume the
 * parser could not read). A scored chip is clickable and opens the full breakdown.
 */
// Scoring runs asynchronously after submit (the RAG indexer scores the resume as it
// indexes it), so a freshly added candidate has no score for a short while.
const SCORE_PENDING_WINDOW_MS = 5 * 60 * 1000;

/**
 * Blue-collar (non_resume_based) candidates, and rows with no resume at all, are never
 * scored -- the backend drops the resume for those jobs so the RAG indexer skips them.
 */
export const isNonResumeProfile = (profile) =>
  !!profile && (
    profile.pipeline_type === "non_resume_based" ||
    profile.JobPost?.pipeline_type === "non_resume_based" ||
    (!profile.url && !profile.profile_path && !profile.resume_url)
  );

/** True for a recently added resume profile whose score has not been written yet. */
export const isScorePending = (profile) => {
  if (!profile || scoreToPercent(profile.resume_score) !== null) return false;
  if (isNonResumeProfile(profile)) return false;
  const created = new Date(profile.createdAt || profile.posted_on).getTime();
  return Number.isFinite(created) && Date.now() - created < SCORE_PENDING_WINDOW_MS;
};

export const ResumeScoreChip = ({ score, status, reason, explanationHtml, pending = false, notApplicable = false }) => {
  const [open, setOpen] = useState(false);
  const percent = scoreToPercent(score);

  if (percent === null && notApplicable) {
    return (
      <Tooltip title="Not applicable: no resume for this job type">
        <span
          style={{
            color: "#9ca3af",
            fontWeight: 500,
            fontSize: "0.8rem",
            display: "inline-block",
            minWidth: 58,
            textAlign: "center",
          }}
        >
          N/A
        </span>
      </Tooltip>
    );
  }

  if (percent === null && pending) {
    return (
      <Tooltip title="Resume is being scored. This updates automatically.">
        <span
          style={{
            color: "#6b7280",
            fontStyle: "italic",
            fontSize: "0.8rem",
            display: "inline-block",
            minWidth: 58,
            textAlign: "center",
          }}
        >
          Scoring…
        </span>
      </Tooltip>
    );
  }

  if (percent === null) {
    return (
      <Tooltip title="Not scored yet">
        {/* Same footprint as the chip below, so scored and unscored rows line up */}
        <span
          style={{
            color: "#9ca3af",
            fontWeight: 500,
            display: "inline-block",
            minWidth: 58,
            textAlign: "center",
          }}
        >
          –
        </span>
      </Tooltip>
    );
  }

  const { color, bg, border } = scoreColor(percent);

  return (
    <>
      <Chip
        label={`${percent}%`}
        size="small"
        onClick={() => setOpen(true)}
        sx={{
          color,
          backgroundColor: bg,
          border: `1px solid ${border}`,
          fontWeight: 600,
          minWidth: 58,
          cursor: "pointer",
        }}
      />
      <ResumeScoreSummaryDialog
        open={open}
        onClose={() => setOpen(false)}
        status={status}
        reason={reason}
        explanationHtml={explanationHtml}
      />
    </>
  );
};

/**
 * Filter + sort controls for the score column.
 * Both map straight onto the query params the list endpoints accept:
 *   selectionStatus, minScore, sortBy=resume_score, sortOrder
 */
// Matches the height, radius and fill of the custom search box these filters sit beside,
// so the MUI selects don't read as bolted-on controls.
const fieldSx = (minWidth) => ({
  minWidth,
  "& .MuiOutlinedInput-root": {
    height: 42,
    borderRadius: "8px",
    backgroundColor: "#F9FAFB",
    fontSize: "0.875rem",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
});

export const ResumeScoreFilters = ({
  selectionStatus = "",
  minScore = "",
  sortOrder = "",
  onChange,
  size = "small",
}) => (
  <>
    <TextField
      select
      size={size}
      label="Selection"
      value={selectionStatus}
      onChange={(e) => onChange({ selectionStatus: e.target.value })}
      sx={fieldSx(145)}
    >
      <MenuItem value="">All</MenuItem>
      <MenuItem value="Selected">Selected</MenuItem>
      <MenuItem value="Not-Selected">Not-Selected</MenuItem>
    </TextField>

    <TextField
      select
      size={size}
      label="Min score"
      value={minScore}
      onChange={(e) => onChange({ minScore: e.target.value })}
      sx={fieldSx(120)}
    >
      <MenuItem value="">Any</MenuItem>
      {/* Values are raw scores; labels are the percentages the user sees */}
      <MenuItem value="4">40%+</MenuItem>
      <MenuItem value="6">60%+</MenuItem>
      <MenuItem value="8">80%+</MenuItem>
    </TextField>

    <TextField
      select
      size={size}
      label="Sort by score"
      value={sortOrder}
      onChange={(e) => onChange({ sortOrder: e.target.value })}
      sx={fieldSx(150)}
    >
      <MenuItem value="">Default</MenuItem>
      <MenuItem value="DESC">Highest first</MenuItem>
      <MenuItem value="ASC">Lowest first</MenuItem>
    </TextField>
  </>
);

export default ResumeScoreChip;
